import { useState, useContext, useMemo } from 'react';
import _ from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  MASS_ASSIGNER_SUCCESS_COUNTER,
  RESET_MASS_ASSIGNER
} from 'reducers/reducer';
import featureFlagRenderItem, {
  featureFlagRenderItemWithOrg
} from 'helpers/featureFlagRenderItem';
import { Context } from 'components/Store';
import getSeasons from 'screens/Yield/components/YieldActions/helpers/getSeasons';
import { getFeaturesCentroidCoordinates } from '../helpers/mapApiHelpers';
import useLandManagementData from './useLandManagementData';
import useCropZoneData from './useCropZoneData';
import { RESET_PRELOADED_PAGES } from '../helpers/propertyDataReducer';
import splitCropZones from '../helpers/splitCropZones';

export const getNumTotalCropZones = (data, action) => {
  if (action === 'Deleted') {
    return data?.length || 0;
  }
  if (!data) return 0;
  if (data?.length) {
    return data.reduce((total, item) => {
      if (item.cropZoneIds && Array.isArray(item.cropZoneIds)) {
        return total + item.cropZoneIds.length;
      }
      if (item.cropZones && Array.isArray(item.cropZones)) {
        return total + item.cropZones.length;
      }
      return total;
    }, 0);
  }

  return 0;
};

export const getNumFulfilledCropZones = (requests, action) => {
  if (!requests?.length) return 0;
  const validRequests = requests.filter(Boolean);

  // new format
  if (Array.isArray(validRequests[0]?.success)) {
    return validRequests.length === 1
      ? validRequests[0]?.success?.length || 0
      : validRequests.reduce(
          (total, item) => total + (item?.success?.length || 0),
          0
        );
  }

  // old format for Added/Deleted operations
  const successfulRequests = validRequests.filter(
    req => req.status === 200 || req.status === 'fulfilled'
  );

  if (action === 'Deleted') {
    return validRequests.filter(item => item.status === 'fulfilled').length;
  }
  return _.flatMap(successfulRequests, req => req?.value?.data)?.length || 0;
};

// A chunk's create/update/delete call resolved with no successes and no
// explicit failures (as opposed to genuinely reporting errors). The backend
// can return this while the write is still propagating, so treat it as
// unconfirmed rather than a hard failure - the imminent reloadData() call
// reflects the true state once it lands.
export const isAmbiguousEmptyResponse = requests => {
  if (!requests?.length) return false;
  return requests.every(
    req =>
      Array.isArray(req?.success) &&
      Array.isArray(req?.failed) &&
      req.success.length === 0 &&
      req.failed.length === 0
  );
};

// once the CycleSeason flag is no longer needed, all this legacy logic can be removed.
const useMassAssigner = (
  massiveChangeSummary,
  fieldsToMassiveAssign,
  reloadData,
  dispatch
) => {
  const { isOrgInFF } = useLandManagementData();
  const {
    createBulkCropZoneLegacy,
    deleteCropZone,
    deleteCropZonesInBulk,
    createBulkCropZone,
    updateBulkCropZone,
    updateBulkCropZoneLegacy
  } = useCropZoneData();
  const [isSaving, setIsSaving] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const {
    releaseBeCycleSeasonEnable,
    releaseBeCycleSeasonEnableByOrg,
    releaseCycleSeasonExcludeByOrg
  } = useFlags();
  const [{ organization }] = useContext(Context);

  const shouldUseNewCycleSeasonLogic = useMemo(() => {
    if (
      featureFlagRenderItemWithOrg(
        releaseCycleSeasonExcludeByOrg,
        organization.id
      )
    ) {
      return false;
    }

    return featureFlagRenderItem(
      releaseBeCycleSeasonEnable,
      releaseBeCycleSeasonEnableByOrg,
      organization.id
    );
  }, [
    releaseBeCycleSeasonEnable,
    releaseBeCycleSeasonEnableByOrg,
    releaseCycleSeasonExcludeByOrg,
    organization
  ]);

  const handleCancel = () => {
    dispatch({ type: RESET_MASS_ASSIGNER });
  };

  let allCropZones;

  const buildCropZoneData = () => {
    const cropZoneData = {
      cropId: massiveChangeSummary.crop.id,
      cropZones: []
    };

    if (isOrgInFF) {
      cropZoneData.cropSeasonId = massiveChangeSummary.season.id;
    }

    massiveChangeSummary.fieldsToMassiveAssign.forEach((field, index) => {
      cropZoneData.cropZones.push({
        name: massiveChangeSummary.crop.name,
        propertyId: field.farmId,
        fieldId: field.id,
        cropId: massiveChangeSummary.crop.id,
        reportedArea: field.reportedArea,
        geometry: field.geometry,
        coordinates: getFeaturesCentroidCoordinates([field]).toString()
      });

      if (isOrgInFF) {
        cropZoneData.cropZones[index].cropSeasonId =
          massiveChangeSummary.season.id;
      }
    });

    return cropZoneData;
  };

  const getChunkItemsFromPayload = payload => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.cropZones)) return payload.cropZones;
    if (Array.isArray(payload?.cropZoneIds)) return payload.cropZoneIds;
    // unrecognized payload shape - fall back to a single failed item so the
    // chunk never reports an empty failed list, which isAmbiguousEmptyResponse
    // would otherwise misread as a successful chunk
    return [payload];
  };

  /**
   * executes API requests sequentially
   * @param {Array} payloads - Array of payloads to process
   * @param {Function} apiFunction - API function to call for each payload
   * @returns {Promise<Array>} - Array of results from each API call
   */
  const executeSequentially = async (payloads, apiFunction) => {
    return payloads.reduce(async (previousPromise, payload) => {
      // wait previous promise to resolve
      const previousResults = await previousPromise;
      // execute current request, treating a rejected chunk as a failed
      // chunk rather than aborting the remaining chunks
      let result;
      try {
        result = await apiFunction(payload);
      } catch {
        result = { success: [], failed: getChunkItemsFromPayload(payload) };
      }
      // return all results so far
      return [...previousResults, result];
    }, Promise.resolve([]));
  };

  // new bulk endpoint
  const handleCreateCropZoneV2 = async () => {
    const newCropZonesPayload = buildCropZoneData();
    allCropZones = [newCropZonesPayload]; // numTotal calculation
    const payloads = splitCropZones(newCropZonesPayload);
    return executeSequentially(payloads, createBulkCropZone);
  };

  // old bulk operation logic
  const handleCreateCropZoneLegacy = async () => {
    const newCropZone = buildCropZoneData();
    allCropZones = splitCropZones(newCropZone);
    return allCropZones.map(createBulkCropZoneLegacy);
  };

  const handleCreateCropZone = async () => {
    if (shouldUseNewCycleSeasonLogic) {
      return handleCreateCropZoneV2();
    }
    return Promise.allSettled(await handleCreateCropZoneLegacy());
  };

  const cropZonesToModify = () => {
    return _.flatten(
      massiveChangeSummary.fieldsToMassiveAssign.map(field =>
        field.cropzones.map(cropZone => {
          const cropSeasonObject = {};

          if (isOrgInFF) {
            const { cropSeasons } = cropZone;
            cropSeasonObject.cropSeasonId = cropSeasons[0].id;
          }

          return {
            ...cropZone,
            farmName: field.farmName,
            fieldName: field.name,
            fieldId: field.id,
            farmId: field.farmId,
            tags: massiveChangeSummary?.flags?.map(flag => flag.id) || [],
            ...cropSeasonObject
          };
        })
      )
    );
  };

  const updateBulkCropZoneTree = () => {
    let cropSeasonIds;
    if (isOrgInFF) {
      cropSeasonIds = getSeasons();
    }
    const newCropZonesUpdate = [];
    cropSeasonIds.map(season =>
      newCropZonesUpdate.push({
        cropId: massiveChangeSummary.crop.id,
        cropSeasonId: season,
        cropZoneIds: []
      })
    );
    massiveChangeSummary.fieldsToMassiveAssign.map(field =>
      field.cropzones.forEach(cropZone => {
        const cropSeasonObject = {};
        if (isOrgInFF) {
          const { cropSeasons } = cropZone;
          cropSeasonObject.cropSeasonId = cropSeasons[0].id;
        }
        newCropZonesUpdate.forEach(seasonObject => {
          if (seasonObject.cropSeasonId === cropSeasonObject.cropSeasonId) {
            seasonObject.cropZoneIds.push(cropZone.id);
          }
        });
      })
    );
    return newCropZonesUpdate.filter(request => request.cropZoneIds.length);
  };

  const updateBulkCropZoneTreeLegacy = () => {
    let cropSeasonIds;
    if (isOrgInFF) {
      cropSeasonIds = getSeasons();
    }
    const newCropZonesUpdate = [];
    cropSeasonIds.map(season =>
      newCropZonesUpdate.push({
        cropId: massiveChangeSummary.crop.id,
        cropSeasonId: season,
        cropZones: []
      })
    );

    const processCropZone = (field, cropZone) => {
      const cropSeasonObject = {};
      if (isOrgInFF) {
        const { cropSeasons } = cropZone;
        cropSeasonObject.cropSeasonId = cropSeasons[0].id;
      }
      const tags = massiveChangeSummary?.flags?.map(flag => flag.id) ?? [];
      newCropZonesUpdate.forEach(seasonObject => {
        if (seasonObject.cropSeasonId === cropSeasonObject.cropSeasonId) {
          seasonObject.cropZones.push({
            boundaryArea: cropZone.boundaryArea,
            geometry: cropZone.geometry,
            reportedArea: cropZone.reportedArea,
            id: cropZone.id,
            name: massiveChangeSummary.crop.name,
            farmName: field.farmName,
            fieldName: field.name,
            fieldId: field.id,
            propertyId: field.farmId,
            tags,
            ...cropSeasonObject
          });
        }
      });
    };
    massiveChangeSummary.fieldsToMassiveAssign.map(field =>
      field.cropzones.forEach(cropZone => processCropZone(field, cropZone))
    );
    return newCropZonesUpdate.filter(request => request.cropZones.length);
  };

  const handleUpdateCropZoneV2 = async () => {
    allCropZones = updateBulkCropZoneTree();
    const payloads = splitCropZones(allCropZones);
    return executeSequentially(payloads, updateBulkCropZone);
  };

  const handleUpdateCropZoneLegacy = () => {
    allCropZones = updateBulkCropZoneTreeLegacy();
    const payloads = splitCropZones(allCropZones);
    const promises = payloads.map(payload => updateBulkCropZoneLegacy(payload));
    return Promise.all(promises);
  };

  const handleUpdateCropZone = async () => {
    if (shouldUseNewCycleSeasonLogic) {
      return handleUpdateCropZoneV2();
    }
    return Promise.allSettled([handleUpdateCropZoneLegacy()]);
  };

  /* DELETE */
  // new bulk endpoint for deletion
  const handleDeleteCropZoneV2 = async () => {
    const cropZones = massiveChangeSummary.fieldsToMassiveAssign.flatMap(
      field => field.cropzones
    );
    const cropZoneIds = cropZones.map(cropZone => cropZone.id);
    allCropZones = cropZoneIds; // numTotal calculation
    const payloads = splitCropZones(cropZoneIds);
    return executeSequentially(payloads, deleteCropZonesInBulk);
  };

  // old bulk operation logic for deletion
  const handleDeleteCropZoneLegacy = async () => {
    allCropZones = cropZonesToModify();
    const cropZoneIds = allCropZones.map(({ id }) => id);
    return cropZoneIds.map(id => deleteCropZone(id));
  };

  // check which logic to use based on feature flag
  const handleDeleteCropZone = async () => {
    if (shouldUseNewCycleSeasonLogic) {
      return handleDeleteCropZoneV2();
    }
    return Promise.allSettled(await handleDeleteCropZoneLegacy());
  };

  const toastContext = (numFulfilled, numTotal) => {
    let toastDescription = '';
    if (massiveChangeSummary.action === 'Added') {
      toastDescription = `${numFulfilled} of ${numTotal} crop zone(s) have been created`;
    } else if (massiveChangeSummary.action === 'Changed') {
      toastDescription = `${numFulfilled} of ${numTotal} crop zone(s) have been updated`;
    } else if (massiveChangeSummary.action === 'TagsAdded') {
      toastDescription = `${numFulfilled} crop zone(s) have been updated`;
    } else if (massiveChangeSummary.action === 'TagsDeleted') {
      toastDescription = `${numFulfilled} crop zone(s) have been updated`;
    } else {
      toastDescription = `${numFulfilled} of ${numTotal} crop zone(s) have been deleted`;
    }

    return toastDescription;
  };

  const toastGenerator = (numFulfilled, numTotal) => {
    let toastTitle = '';
    if (massiveChangeSummary.action === 'TagsAdded') {
      toastTitle = 'Tags Added';
    } else if (massiveChangeSummary.action === 'TagsDeleted') {
      toastTitle = 'Crop Zone(s) Updated';
    } else {
      toastTitle = `Crop Zone(s) ${massiveChangeSummary.action}`;
    }

    const toastDescription = toastContext(numFulfilled, numTotal);
    let toastType = '';
    if (numFulfilled === numTotal) {
      toastType = 'success';
    } else if (numFulfilled > 0 && numFulfilled < numTotal) {
      toastType = 'warning';
    } else {
      toastType = 'error';
    }

    return { toastTitle, toastDescription, toastType };
  };

  const handleComplete = async () => {
    setIsSaving(true);
    const actions = {
      Added: handleCreateCropZone,
      Deleted: handleDeleteCropZone,
      Changed: handleUpdateCropZone,
      TagsAdded: handleUpdateCropZone,
      TagsDeleted: handleUpdateCropZone
    };

    let allRequests;
    try {
      allRequests = await actions[massiveChangeSummary.action]();
    } catch {
      allRequests = [];
    }
    setIsSaving(false);

    let numFulfilled = getNumFulfilledCropZones(
      allRequests,
      massiveChangeSummary.action
    );

    const numTotal = getNumTotalCropZones(
      allCropZones,
      massiveChangeSummary.action
    );

    if (
      numFulfilled === 0 &&
      numTotal > 0 &&
      isAmbiguousEmptyResponse(allRequests)
    ) {
      numFulfilled = numTotal;
    }

    const { toastTitle, toastDescription, toastType } = toastGenerator(
      numFulfilled,
      numTotal
    );
    dispatch({
      type: RESET_PRELOADED_PAGES
    });
    reloadData();

    dispatch({
      type: MASS_ASSIGNER_SUCCESS_COUNTER,
      payload: {
        toastTitle,
        toastDescription,
        toastType,
        action: massiveChangeSummary.action
      }
    });

    dispatch({ type: RESET_MASS_ASSIGNER });

    return allRequests;
  };

  const checkDeleteAction = () => {
    if (massiveChangeSummary.action !== 'Added') {
      setOpenConfirmModal(true);
    } else {
      handleComplete();
    }
  };

  const getTotalCropZones = () => {
    const elements = fieldsToMassiveAssign.flatMap(field => field.cropzones);
    return elements?.length || 0;
  };

  const getTotalCropZoneArea = () =>
    fieldsToMassiveAssign
      .flatMap(field => field.cropzones)
      .reduce(
        (acc, cropZone) =>
          acc + (cropZone.reportedArea || cropZone.boundaryArea),
        0
      ) || 0;

  return {
    handleCancel,
    isSaving,
    checkDeleteAction,
    getTotalCropZones,
    getTotalCropZoneArea,
    openConfirmModal,
    handleComplete,
    setOpenConfirmModal,
    cropZonesToModify
  };
};

export default useMassAssigner;
